import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Download, CreditCard, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "@/lib/api";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function ClientInvoice() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [ptoken, setPtoken] = useState("");
  const [inv, setInv] = useState(null);
  const [method, setMethod] = useState("email");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/portal/invoice/${token}`).then(async (r) => {
      if (!r.ok) { setError((await r.json()).detail || "Invoice unavailable"); return; }
      setMeta(await r.json());
    }).catch(() => setError("Unable to load invoice."));
  }, [token]);

  const loadInvoice = async (pt) => {
    const r = await fetch(`${API}/portal/invoice/${token}/data`, { headers: { Authorization: `Bearer ${pt}` } });
    if (r.ok) setInv(await r.json());
  };

  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/portal/invoice/${token}/verify`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ method, value }) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Verification failed"); return; }
      setPtoken(d.portal_token); await loadInvoice(d.portal_token);
    } finally { setBusy(false); }
  };

  const dl = async (path, name) => {
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${ptoken}` } });
    if (!r.ok) return toast.error("Download failed");
    const u = URL.createObjectURL(await r.blob());
    const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
  };

  if (error) return <Shell><div className="text-center py-16"><p className="text-lg font-semibold text-[var(--navy)]">{error}</p><p className="text-sm text-gray-500 mt-2">Please contact us at (321) 376-8744.</p></div></Shell>;
  if (!meta) return <Shell><div className="py-24 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[var(--action)]" /></div></Shell>;

  if (!inv) return (
    <Shell business={meta.business}>
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 text-[var(--navy)] mb-2"><ShieldCheck className="w-5 h-5 text-green-600" /><h2 className="font-bold text-lg">Verify to view your invoice</h2></div>
        <p className="text-sm text-gray-500 mb-4">For your security, confirm the email or billing ZIP code on file{meta.email_hint ? ` (hint: ${meta.email_hint})` : ""}.</p>
        <form onSubmit={verify} className="space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setMethod("email")} className={`flex-1 py-2 rounded-md text-sm font-semibold ${method === "email" ? "bg-[var(--navy)] text-white" : "bg-gray-100"}`} data-testid="verify-email-tab">Email</button>
            <button type="button" onClick={() => setMethod("zip")} className={`flex-1 py-2 rounded-md text-sm font-semibold ${method === "zip" ? "bg-[var(--navy)] text-white" : "bg-gray-100"}`} data-testid="verify-zip-tab">ZIP Code</button>
          </div>
          <input className="w-full h-11 px-3 rounded-md border border-gray-300" placeholder={method === "email" ? "you@email.com" : "ZIP code"} value={value} onChange={(e) => setValue(e.target.value)} required data-testid="verify-input" />
          <button disabled={busy} className="w-full bg-[var(--red)] text-white font-semibold py-3 rounded-md" data-testid="verify-submit">{busy ? "Verifying…" : "View Invoice"}</button>
        </form>
      </div>
    </Shell>
  );

  return (
    <Shell business={inv.business}>
      <div className="max-w-2xl mx-auto space-y-4" data-testid="client-invoice">
        <div className="flex items-center justify-between">
          <div><h1 className="text-2xl font-extrabold text-[var(--navy)]">Invoice {inv.invoice_number}</h1>
            <p className="text-sm text-gray-500">{inv.invoice_date} · Due {inv.due_date || "—"} · <span className="font-semibold">{inv.status}</span></p></div>
          <button onClick={() => dl(`/portal/invoice/${token}/pdf`, `${inv.invoice_number}.pdf`)} className="text-sm text-[var(--action)] flex items-center gap-1" data-testid="client-pdf"><Download className="w-4 h-4" /> PDF</button>
        </div>

        <div className="bg-white rounded-xl border p-5">
          {inv.service_description && <p className="text-sm text-gray-600 mb-3">{inv.service_description}</p>}
          <table className="w-full text-sm">
            <tbody>{(inv.line_items || []).map((it, i) => (
              <tr key={i} className="border-b border-gray-50"><td className="py-2">{it.description}</td><td className="text-right text-gray-500">{it.qty} × {money(it.rate)}</td><td className="text-right font-semibold w-24">{money(it.qty * it.rate)}</td></tr>
            ))}</tbody>
          </table>
          <div className="mt-3 text-sm space-y-1">
            <Row l="Subtotal" v={money(inv.subtotal)} />{inv.discount ? <Row l="Discount" v={`-${money(inv.discount)}`} /> : null}{inv.tax ? <Row l="Tax" v={money(inv.tax)} /> : null}
            <Row l="Total" v={money(inv.total)} bold /><Row l="Amount Paid" v={money(inv.amount_paid)} /><Row l="Balance Due" v={money(inv.balance_due)} bold />
          </div>
          {inv.payment_instructions && <p className="text-xs text-gray-500 mt-3">{inv.payment_instructions}</p>}
        </div>

        {inv.receipts?.length > 0 && (
          <div className="bg-white rounded-xl border p-5">
            <h3 className="font-bold text-[var(--navy)] mb-2">Receipts</h3>
            {inv.receipts.map((rc) => (
              <button key={rc.id} onClick={() => dl(`/portal/invoice/${token}/receipt/${rc.id}`, `${rc.receipt_number}.pdf`)} className="flex items-center gap-2 text-sm text-[var(--action)] py-1" data-testid={`client-receipt-${rc.receipt_number}`}><Download className="w-4 h-4" /> {rc.receipt_number} · {money(rc.amount)}</button>
            ))}
          </div>
        )}

        {inv.balance_due > 0 && inv.status !== "Voided" && <BillingAndPay token={token} ptoken={ptoken} inv={inv} onDone={() => loadInvoice(ptoken)} />}

        {inv.financing_option && <Financing token={token} ptoken={ptoken} />}

        <p className="text-center text-sm text-gray-500 pt-2"><a href="tel:+13213768744" className="inline-flex items-center gap-1 text-[var(--navy)] font-semibold"><Phone className="w-4 h-4 text-[var(--red)]" /> (321) 376-8744</a></p>
      </div>
    </Shell>
  );
}

function BillingAndPay({ token, ptoken, inv, onDone }) {
  const [f, setF] = useState({ first_name: "", last_name: "", billing_address: "", city: "", state: "", zip: "", phone: "", email: "", payment_method_preference: "Credit or debit card", authorization_ack: false });
  const [typedName, setTypedName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const H = { "Content-Type": "application/json", Authorization: `Bearer ${ptoken}` };
  const submit = async () => {
    setBusy(true);
    try {
      const rb = await fetch(`${API}/portal/invoice/${token}/billing`, { method: "POST", headers: H, body: JSON.stringify(f) });
      if (!rb.ok) { toast.error("Could not save billing info"); return; }
      const ra = await fetch(`${API}/portal/invoice/${token}/authorize`, { method: "POST", headers: H, body: JSON.stringify({ authorized_amount: inv.balance_due, typed_name: typedName, ack, payment_method_preference: f.payment_method_preference }) });
      const d = await ra.json();
      if (!ra.ok) { toast.error(d.detail || "Authorization failed"); return; }
      setDone(true); toast.success("Submitted securely"); onDone?.();
    } finally { setBusy(false); }
  };
  if (done) return <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center" data-testid="pay-done"><CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" /><p className="font-semibold text-green-800">Thank you! Our team will securely process your payment and email your receipt.</p></div>;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="billing-form">
      <div className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-[var(--navy)]" /><h3 className="font-bold text-[var(--navy)]">Billing Information & Authorization</h3></div>
      {!inv.card_collection_enabled && <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800" data-testid="card-disabled-note">Secure card entry is not enabled yet. Submit your billing details and authorize the amount — our team will securely collect and process your card.</p>}
      <div className="grid grid-cols-2 gap-2">
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm" placeholder="First name" value={f.first_name} onChange={set("first_name")} data-testid="bf-first" />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm" placeholder="Last name" value={f.last_name} onChange={set("last_name")} />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm col-span-2" placeholder="Billing address" value={f.billing_address} onChange={set("billing_address")} />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm" placeholder="City" value={f.city} onChange={set("city")} />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm" placeholder="State" value={f.state} onChange={set("state")} />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm" placeholder="ZIP" value={f.zip} onChange={set("zip")} />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm" placeholder="Phone" value={f.phone} onChange={set("phone")} />
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm col-span-2" placeholder="Email" value={f.email} onChange={set("email")} />
        <select className="h-10 px-3 rounded-md border border-gray-300 text-sm col-span-2" value={f.payment_method_preference} onChange={set("payment_method_preference")}>
          {["Credit or debit card", "ACH or bank payment", "Check", "Cash", "Financing through EasyQuickFund", "Other"].map((m) => <option key={m}>{m}</option>)}
        </select>
      </div>
      {/* Card fields would be hosted by the secure vault provider — intentionally not rendered as plain fields */}
      <div className="border-t pt-3 text-xs text-gray-600">
        <p className="mb-2">I authorize {money(inv.balance_due)} to be processed by {inv.business?.name} through its approved payment processor. My billing information is accurate. Financing applications are handled separately through EasyQuickFund and approval is not guaranteed.</p>
        <input className="h-10 px-3 rounded-md border border-gray-300 text-sm w-full mb-2" placeholder="Type your full name to authorize" value={typedName} onChange={(e) => setTypedName(e.target.value)} data-testid="auth-name" />
        <label className="flex items-center gap-2"><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="auth-ack" /> I agree to the authorization above.</label>
      </div>
      <button disabled={busy || !ack || !typedName} onClick={submit} className="w-full bg-[var(--red)] text-white font-semibold py-3 rounded-md disabled:opacity-50" data-testid="submit-authorization">{busy ? "Submitting…" : `Submit & Authorize ${money(inv.balance_due)}`}</button>
    </div>
  );
}

function Financing({ token, ptoken }) {
  const [info, setInfo] = useState(null);
  const apply = async () => {
    const r = await fetch(`${API}/portal/invoice/${token}/financing`, { method: "POST", headers: { Authorization: `Bearer ${ptoken}` } });
    if (r.ok) { setInfo(await r.json()); toast.success("Financing referral started"); }
  };
  return (
    <div className="bg-white rounded-xl border p-5" data-testid="financing-block">
      <h3 className="font-bold text-[var(--navy)] mb-1">Financing Options</h3>
      <p className="text-xs text-gray-500 mb-3">Financing options may be available through EasyQuickFund. Approval and terms are determined by the financing provider.</p>
      {info ? (
        <a href={info.financing_url} target="_blank" rel="noreferrer" className="inline-block bg-[var(--action)] text-white font-semibold py-2 px-4 rounded-md text-sm" data-testid="financing-continue">Continue to EasyQuickFund →</a>
      ) : (
        <button onClick={apply} className="bg-gray-100 hover:bg-gray-200 text-[var(--navy)] font-semibold py-2 px-4 rounded-md text-sm" data-testid="apply-financing">Apply Through EasyQuickFund</button>
      )}
    </div>
  );
}

const Row = ({ l, v, bold }) => <div className={`flex justify-between ${bold ? "font-bold text-[var(--navy)]" : "text-gray-600"}`}><span>{l}</span><span>{v}</span></div>;

function Shell({ children, business }) {
  return (
    <div className="min-h-screen bg-[var(--surface)]">
      <header className="bg-[var(--navy)] text-white py-4 px-5 text-center">
        <img src="/brand/logo.png" alt="" className="h-12 w-12 rounded-lg mx-auto mb-1" />
        <p className="font-head font-bold uppercase text-sm">{business?.name || "Veterans Power Wash & Cleaning Services"}</p>
      </header>
      <main className="p-5">{children}</main>
    </div>
  );
}
